import ProductSubscribeButton from "@auer-steel/CustomComponents/Subscriptions/ProductSubscribeButton";
import ProductUnsubscribeButton from "@auer-steel/CustomComponents/Subscriptions/ProductUnsubscribeButton";
import updateSubscription from "@auer-steel/Handlers/UpdateSubscription";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import ProductInfoListsReducer from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListsReducer";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    updateSubscription: makeHandlerChainAwaitable(updateSubscription),
};

const ProductSubscriptionButtonGroup: React.FC<Props> = ({
    session,
    productContext: {
        product,
        productInfo: { qtyOrdered, unitOfMeasure, inventory },
    },
    updateSubscription,
}) => {
    if (!product) {
        return null;
    }

    const toasterContext = React.useContext(ToasterContext);
    const [userIsSubscribed, setUserIsSubscribed] = React.useState(product.userIsSubscribed);

    const unSubscribeClickHandler = async () => {
        await updateSubscription({
            productId: product.productNumber,
            subscribed: false,
        });
        toasterContext.addToast({
            body: siteMessage("Subscription_ProductUnsubscribed"),
            messageType: "success",
            timeoutLength: 6000,
        });
        setUserIsSubscribed(false);
    };

    const subscribeClickHandler = async () => {
        const result = await updateSubscription({
            productId: product.productNumber,
            subscribed: true,
        });
        setUserIsSubscribed(true);
        toasterContext.addToast({
            body: siteMessage("Subscription_ProductSubscribed"),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    const hasInventory = inventory?.qtyOnHand !== undefined && inventory.qtyOnHand > 0;

    return (
        <StyledWrapper style={{ textAlign: "start" }}>
            {session.isAuthenticated &&
                userIsSubscribed &&
                (product.warehouses === undefined ||
                    !product.warehouses!.find(warehouse => warehouse.qtyAvailable > 0)) && (
                    <ProductUnsubscribeButton handler={unSubscribeClickHandler} />
                )}
            {session.isAuthenticated && !userIsSubscribed && !hasInventory && !product.isDiscontinued && (
                <ProductSubscribeButton handler={subscribeClickHandler} />
            )}
        </StyledWrapper>
    );
};

const productSubscriptionModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductSubscriptionButtonGroup)),
    definition: {
        group: "Products",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default productSubscriptionModule;
